import * as storage from '../../utils/storage';
import Animated, { BounceIn } from 'react-native-reanimated';
import DesignSystemButton from '../Button/DesignSystemButton';
import Show from '../Show/Show';
import { ActivityIndicator, DeviceEventEmitter, Keyboard, Pressable, View } from 'react-native';
import { COLORS, GRADIENTS, ModuleGradient } from '../../config/designSystem/Colors';
import { ChallengeCongratsFrame } from '../Challenges/ChallengeCongratsFrame';
import { ConfirmDialog } from '../Modal/ConfirmDialog';
import { ContainDefault } from './Default/ContainDefault';
import { ContentFrameTree } from '../Content/ContentFrame/ContentFrameTree';
import { FC, useRef, useState } from 'react';
import { FEEDBACK_WITH_JSON } from '../../state/mutations/User';
import { PresentDefault } from './Default/PresentDefault';
import { RECORD_ACTIVITY_ENTRY, UPDATE_ACTIVITY_ENTRY } from '../../state/mutations/UserActivity';
import { SET_ONBOARDING_DONE, SET_REDO_SESSION_DONE } from '../../state/mutations/User';
import { SessionButtons } from '../SessionButtons/SessionButtons';
import { activityKey, contentStackKey, libraryStackKey, privateKey } from '../../constants/routeKeys';
import { addTrigger } from '../../utils/onesignal/addTrigger';
import { captureException } from '../../utils/sentry/sentry';
import { composeActivityEntry } from '../../utils/activities/composeActivityEntry';
import { concatPath } from '../../utils/links/concatPath';
import { config } from './config';
import { consumeNextPress } from '../../utils/session/nextInterceptor';
import { goTo } from '../../utils/links/goTo';
import { handleEndOfInstantHelpSurvey } from '../../utils/survey/handleEndOfInstantHelpSurvey';
import { handleEndOfSchedulerSurvey } from '../../utils/survey/handleEndOfSchedulerSurvey';
import { isWeb } from '../../config/designSystem/Layout';
import { logAmplitude, setProperty } from '../../utils/analytics/analytics';
import { observer } from 'mobx-react';
import { redirectStringForStore } from '../../constants/storageKeys';
import { router } from 'expo-router';
import { trackActivityEntryRecorded, trackChallengeDayCompleted } from '../../utils/analytics/challengeEvents';
import { useContentMaxWidth } from '../../hooks/ui/useContentMaxWidth';
import { useIsPrivate } from '../../hooks/useIsPrivate';
import { useMutation } from '@apollo/client';
import { useOnMount } from '../../hooks/useOnMount';
import { useSpecificOnNextLogic } from '../QuestionSessions/QuestionSession/useSpecificOnNextLogic';
import { useStores } from '../../hooks/useStores';

const getGlobalTimeSpent = (times: any) => {
  if (times && times.length > 0) {
    return times.reduce((acc: number, cur: number) => {
      if (isNaN(acc) || isNaN(cur)) {
        return 0;
      } else {
        return acc + (cur / 1000);
      }
    }, 0);
  } else {
    return 0;
  }
};

interface SessionProps {
  sessionId: string;
  pathId?: string | null;
  reckoningId?: string;
  fromPath?: boolean
}

// TODO move reckoning logic to hook
// TODO handle segments
// TODO compare network requests with old version for different sessions

/**
 * Session:
 *  - handle session completion
 *  - define nav targets on session exit
 *  - select Container and Presentation based on session step data
 */
const _Session: FC<SessionProps> = ({
  sessionId,
  pathId,
  fromPath,
  reckoningId
}) => {
  const {
    session: sessionStore,
    path: pathStore,
    reckoning: reckoningStore,
    treeNav,
    trees,
    ...rootStore
  } = useStores();
  const inPrivate = useIsPrivate();
  const contentMaxWidth = useContentMaxWidth();
  const path = pathStore.getPath(pathId as string);
  const session = sessionStore.getSessionInfo(pathId || null, sessionId);
  const reckoning = reckoningStore.getReckoning(reckoningId as string);
  const [loading, setLoading] = useState(false);
  // finished a session of a path that is not the active one while another
  // path is: the user is asked whether to switch before leaving
  const [switchPathPrompt, setSwitchPathPrompt] = useState(false);
  // set when a challenge day was recorded at the end of an activity session:
  // the congrats renders as the session's final frame (figma 9484-50206/52395)
  const [challengeEnd, setChallengeEnd] = useState<null | {
    days: { done: boolean }[];
    designSystemColor?: string;
    completed: boolean;
  }>(null);
  // a redirectCallback given to handleEndOfSession must survive the congrats
  // detour and still run when the user taps its Finish button
  const challengeEndRedirectRef = useRef<(() => void) | undefined>(undefined);
  const stepType = treeNav.content.stepType;
  console.log('render Session', {
    stepType: `${stepType}`,
  });

  // per-question side effects of the assistance flows (feedback on an
  // unhappy answer, camera permission), formerly in the survey screen
  const specificOnNextLogic = useSpecificOnNextLogic(treeNav.content.question?.key);
  const [feedbackWithJson] = useMutation(FEEDBACK_WITH_JSON, { fetchPolicy: 'no-cache' });

  const ASSISTANCE_EVENTS: Record<string, { start: string; stop: string }> = {
    instantHelp: { start: 'Instant Help Survey Started', stop: 'Instant Help Survey Exited' },
    scheduling: { start: 'Scheduler Survey Started', stop: 'Scheduler Survey Exited' },
  };
  const isAssistanceSession = ['matching', 'instantHelp', 'scheduling'].includes(session?.type as string);
  useOnMount(() => {
    const events = ASSISTANCE_EVENTS[session?.type as string];
    if (events) {
      // fetch user profile to get latest registeredWithWpo
      rootStore.user.fetchProfile(rootStore.language.language);
      logAmplitude(events.start);
    }
  });

  const cleanUpBeforeExitingReckoning = (exitingBeforeFinishing: boolean) => {
    if (inPrivate) {
      return;
    }
    storage.remove(redirectStringForStore);
    if (exitingBeforeFinishing) {
      storage.remove('token');
      rootStore.clear();
      if (isWeb) {
        window.location.reload();
      }
    }
  };

  const redirectForReckoning = (exitingBeforeFinishing: boolean) => {
    const redirectionToRecommended = reckoning?.redirectionPage === 'RECOMMENDED_RESULT';
    cleanUpBeforeExitingReckoning(exitingBeforeFinishing);
    if (redirectionToRecommended && !exitingBeforeFinishing) {
      goTo.reckoningResult(
        reckoning.resultFeature as string,
        reckoning.resultRecommendationStrategy as string,
        inPrivate,
      );
    } else {
      if (inPrivate) {
        goTo.dashboard();
      } else {
        if (reckoning?.allowRegistration) {
          goTo.register();
        } else {
          goTo.thankYou();
        }
      }
    }

  };

  // makes this session's path the active one (what "Choose this path" does
  // on the path page), with the same feedback bookkeeping
  const activatePath = () => {
    if (!pathId) {
      return;
    }
    const programExistAndNotFinished = pathStore.programExistAndNotFinished;
    const formerActivePath = rootStore?.user?.activePath;
    logAmplitude('Program Chosen', { pathId: path?.humanReadableId, from: 'session_end' });
    // arms the dashboard tour only for someone who never had it (same rule
    // as the path page): a path SWITCH used to replay the first-path tour
    storage.loadString('activePath').then(value => {
      if (!value) {
        storage.saveString('activePath', 'true');
      }
    });
    rootStore?.user.setActivePath(pathId);
    if (programExistAndNotFinished) {
      rootStore?.user?.setNeedCollectFeedbackAfterProgramChanged(true, formerActivePath);
    }
  };

  const redirect = (exitingBeforeFinishing: boolean) => {
    if (pathId && fromPath) {
      // isActive is a view FUNCTION on the path model: unparenthesised it
      // was always truthy, so every path session ended on the dashboard
      if (path?.isActive?.()) {
        goTo.dashboard();
      } else if (!exitingBeforeFinishing && !pathStore.programExist) {
        // a session done while no path is followed: this path is the one
        activatePath();
        goTo.dashboard();
      } else if (!exitingBeforeFinishing) {
        setSwitchPathPrompt(true);
      } else {
        goTo.path(pathId);
      }
    } else {
      console.log('redirect else');
      if (session?.type === 'redoForAssistanceItem') {
        if (exitingBeforeFinishing) {
          goTo.assistance();
        } else {
          goTo.assistanceOwn();
        }
      } else if (session?.type === 'onboarding') {
        if (exitingBeforeFinishing) {
          goTo.dashboard();
        } else {
          const recommendedCategory = rootStore?.categories.recommendedCategory;
          addTrigger('send_me_push_notification_prompt_please', rootStore?.language?.name?.key);
          goTo.genericPage(recommendedCategory?._id as string);
        }
      } else if (session?.type === 'reckoning') {
        redirectForReckoning(exitingBeforeFinishing);
      } else if (session?.type === 'matching') {
        goTo.assistance();
      } else if (session?.type === 'instantHelp') {
        if (exitingBeforeFinishing) {
          goTo.assistance();
        } else {
          handleEndOfInstantHelpSurvey({ ...rootStore, treeNav } as any, feedbackWithJson);
        }
      } else if (session?.type === 'scheduling') {
        if (!exitingBeforeFinishing) {
          handleEndOfSchedulerSurvey({ ...rootStore, treeNav } as any, feedbackWithJson);
        }
        goTo.assistance();
      } else if (session?.type === 'mhc') {
        goTo.dashboard();
      } else {
        // 'activity' and any other stand-alone session: return to wherever
        // the session was opened from (overview in the Library or Dashboard
        // stack), else home. /session/branching mounts as a SIBLING of the
        // (private) tab navigator, so this only works because that navigator
        // now sets backBehavior="history" — with the default (firstRoute),
        // goBack() from a sibling re-focused the first tab (Health).
        const activityOfSession = rootStore?.activity?.getActivityBySessionId(sessionId);
        if (session?.type === 'activity' && activityOfSession?._id) {
          // the activity page (its entries, calendar), whatever stack it was opened from
          router.navigate({
            pathname: concatPath([privateKey, libraryStackKey, contentStackKey, activityKey], -1) as string,
            params: { id: activityOfSession._id },
          });
        } else if (router.canGoBack()) {
          router.back();
        } else {
          goTo.dashboard();
        }
      }
    }
  };

  const [setOnboardingDoneMutation] = useMutation(SET_ONBOARDING_DONE);
  const [setRedoSessionDoneMutation] = useMutation(SET_REDO_SESSION_DONE);
  const [recordActivityEntryMutation] = useMutation(RECORD_ACTIVITY_ENTRY);
  const [updateActivityEntryMutation] = useMutation(UPDATE_ACTIVITY_ENTRY);

  /**
   * Records this run of the activity. The server decides what it is: the day
   * of the running challenge ('challenge'), or a free entry ('free') when the
   * user just did the activity from the library. Returns the mode, or null
   * when nothing was recorded.
   */
  const recordActivityEntry = async (): Promise<'challenge' | 'free' | null> => {
    const activity = rootStore?.activity?.getActivityBySessionId(sessionId);
    if (!activity?._id) {
      return null;
    }
    const editing = rootStore.activity.entryToEdit;
    if (editing && editing.activityId === activity._id) {
      // rewriting an entry chosen on the activity page: no new occurrence
      rootStore.activity.setEntryToEdit(null);
      try {
        const entry = composeActivityEntry(rootStore, sessionId);
        const { data } = await updateActivityEntryMutation({
          variables: { userActivityId: editing.userActivityId, day: editing.day, entryIndex: editing.entryIndex, entry: entry || null },
        });
        if (data?.updateActivityEntry && editing.day !== null && editing.userActivityId === activity.userActivityId) {
          rootStore.activity.setUserActivity(activity._id, editing.userActivityId, data.updateActivityEntry.status, true, data.updateActivityEntry.days);
        }
      } catch (error) {
        captureException(error);
      }
      rootStore?.responses?.clearSessionResponses(sessionId);
      return 'free';
    }
    try {
      const entry = composeActivityEntry(rootStore, sessionId);
      const { data } = await recordActivityEntryMutation({
        variables: { activityId: activity._id, entry: entry || null },
      });
      const recorded = data?.recordActivityEntry;
      if (!recorded) {
        return null;
      }
      if (recorded.mode === 'challenge' && recorded.userActivity) {
        rootStore.activity.setUserActivity(
          activity._id,
          recorded.userActivity._id,
          recorded.userActivity.status,
          true,
          recorded.userActivity.days,
        );
        trackChallengeDayCompleted(activity, recorded.userActivity, 'activity_session');
      } else if (recorded.mode === 'free') {
        trackActivityEntryRecorded(activity);
      }
      rootStore?.responses?.clearSessionResponses(sessionId);
      return recorded.mode;
    } catch (error) {
      captureException(error);
      return null;
    }
  };

  const handleEndOfSurvey = async() => {
    console.log('END OF SURVEY');
    // the block id comes from the node meta: it is set even when the survey
    // object itself is not in the store yet
    const blockId = treeNav.content.surveyId;
    await sessionStore.endOfSurveyMutation(
      pathId || null,
      sessionId,
      blockId as string,
      reckoningId || null,
    ).catch(console.error);
  };

  // TODO wtf is times ?
  // TODO handle redirect behavior -- maybe in-situ rather than with a callback?
  const handleEndOfSession = async(times?: number[], redirectCallback?: () => void) => {
    DeviceEventEmitter.emit('unload');
    if (pathId || reckoningId) {
      await sessionStore.endOfSession(pathId || null, sessionId, times).catch(console.error);
      if (treeNav.isFinal) {
        logAmplitude('Path Finished', {
          pathId: path?.humanReadableId
        });
      }
    }
    const isReckoning = session?.type === 'reckoning' && !reckoningId;
    const onboardingDoneWhenThisReckoningIsDone = reckoning?.onboardingDoneWhenThisReckoningIsDone;
    if (session?.type === 'onboarding' || (isReckoning && onboardingDoneWhenThisReckoningIsDone)) {
      await setOnboardingDoneMutation().catch(console.error);
      rootStore?.user.onboardingDone();
      await setProperty({ onboardingShown: false });
    }
    if (isReckoning && reckoningId) {
      reckoningStore.setDone(reckoningId);
    }
    if (session?.type === 'redoForAssistanceItem') {
      rootStore?.user.redoSessionDone();
      await setRedoSessionDoneMutation().catch(console.error);
    }
    if (session?.type === 'regular') {
      addTrigger('session_done', rootStore?.language?.name?.key);
    }
    let activityDayRecorded = false;
    if (session?.type === 'activity') {
      // only a challenge day gets the congrats frame; a free entry just ends
      activityDayRecorded = (await recordActivityEntry()) === 'challenge';
    }
    const globalTimeSpent = getGlobalTimeSpent(times);
    logAmplitude('Session Finished', {
      sessionId: session?.humanReadableId,
      pathId: path?.humanReadableId,
      reckoningId: reckoningId,
      timeSpent: globalTimeSpent ? globalTimeSpent : 0,
    });
    if (activityDayRecorded) {
      // the challenge day congrats is a frame INSIDE the session: the user
      // stays in the branching flow and only leaves from its Finish button
      const activity = rootStore?.activity?.getActivityBySessionId(sessionId);
      challengeEndRedirectRef.current = redirectCallback;
      setChallengeEnd({
        days: activity?.days?.slice() || [],
        designSystemColor: activity?.designSystemColor || undefined,
        completed: activity?.status === 'completed',
      });
      return;
    }
    if(redirectCallback) {
      redirectCallback();
    } else {
      redirect(false);
    }
  };

  const onFinish = () => {
    handleEndOfSession().catch(console.error);
  };

  const finishChallengeEnd = () => {
    setChallengeEnd(null);
    const redirectCallback = challengeEndRedirectRef.current;
    challengeEndRedirectRef.current = undefined;
    if (redirectCallback) {
      redirectCallback();
    } else {
      // same exit as any finished session: back to wherever it was opened
      // from (path branch handled inside redirect)
      redirect(false);
    }
  };

  const handleExit = () => {
    const events = ASSISTANCE_EVENTS[session?.type as string];
    if (events) {
      logAmplitude(events.stop, {
        on: treeNav.current,
        path: treeNav.nav?.path.toString(),
      });
    }
    logAmplitude('Session Quitted', {
      session: session?.title,
      pathId,
      path: path?.title,
      reckoningId,
      sessionStepId: treeNav.content.sessionStep?.ownId,
      currentIndex: treeNav.current,
      // TODO
      // timeSpent,
    });
    redirect(true);
    rootStore.responses.clearSessionResponses(sessionId, pathId || undefined);
    // an entry the user chose to rewrite but did not save: dropped, so the
    // next run of this activity records a new occurrence instead
    rootStore.activity?.setEntryToEdit(null);
  };

  const onNext = async () => {
    // TODO sessionStore.sessionStepDone() ???
    // the edge to follow depends on the pending choice: resolve it before
    // that choice is finalised
    const nextKey = treeNav.next;
    if (treeNav.isLastInSurvey) {
      // the last question of a block: its answer must be finalised before
      // the block is scored, then score it now — awaited when the block
      // ends the session, so its outcome (MHC flag, matching) is in before
      // the redirect
      treeNav.choices.finalisePending();
      const scoring = handleEndOfSurvey();
      if (!nextKey) {
        setLoading(true);
        try {
          await scoring;
        } finally {
          setLoading(false);
        }
      }
    }
    if (nextKey) {
      console.log('Session: advancing from', treeNav.current, 'to', nextKey);
      specificOnNextLogic();
      // some block questions provide choices that depend on earlier answers
      if (treeNav.content.nextQuestion?.answersDependOnSurveyResponses) {
        rootStore.question.fetchNextQuestion();
      }
      treeNav.advanceTo(nextKey);
      treeNav.choices.finalisePending();
    } else {
      treeNav.choices.finalisePending();
      onFinish();
    }
  };
  console.log('Session.render current node', treeNav.current, 'next', treeNav.next);

  const handleOnNext = () => {
    // the current frame may consume the press (memo decks page through
    // their cards before the session moves on)
    if (consumeNextPress()) {
      return;
    }
    // handle server-side dynamic next-question
    // (keyed blocks where the next question depends on the answer)
    const fetchDynamicNextQuestion = async () => {
      if (treeNav.content.question?.nextQuestionDependsOnSurveyResponse) {
        const nextQuestion = await rootStore.question.fetchDynamicNextQuestion(treeNav.content.question._id);
        if (nextQuestion?.key) {
          treeNav.choices.setNextQuestion(nextQuestion.key);
        }
      }
    };
    const processUpdatesAndReload = async () => {

      // Update features (local + server)
      treeNav.choices.updateLocalFeature();
      if (treeNav.currentNode?.modifiesTree) {
        treeNav.appendUpdate(treeNav.choices.updateServerFeature(reckoningId));
      } else {
        treeNav.choices.updateServerFeature(reckoningId).catch(captureException);
      }

      try {
        // Only show loading when we need to reload the tree
        // Waiting for updates happens in the background without blocking UI
        if (treeNav.shouldWaitForUpdates) {
          setLoading(true);
          await treeNav.waitForUpdates();
        }
        if (treeNav.shouldReload) {
          setLoading(true);
          await treeNav.reloadTree();
        }
      } catch (error) {
        captureException(error);
      } finally {
        setLoading(false);
      }
    };
    fetchDynamicNextQuestion()
      .catch(captureException)
      .then(() => processUpdatesAndReload())
      .catch(captureException)
      .finally(() => onNext());
  };

  const {
    Container = ContainDefault,
    Presentation = PresentDefault
  } = stepType && config[stepType as keyof typeof config]
    ? config[stepType as keyof typeof config]
    : { Container: ContainDefault, Presentation: PresentDefault };

  // the assessment diary frame is drawn on a soft module gradient, per design
  const isAssessmentFrame = treeNav.content.question?.key === 'assessment';
  const sessionActivity = rootStore?.activity?.getActivityBySessionId(sessionId);
  const assessmentModuleGradient = (GRADIENTS.modules[
    sessionActivity?.designSystemColor || 'LAVENDER'
  ] as ModuleGradient)?.light;
  const assessmentGradient = isAssessmentFrame && assessmentModuleGradient
    ? {
      colors: [assessmentModuleGradient.left, assessmentModuleGradient.right] as [string, string],
      start: { x: 0, y: 0 },
      end: { x: 1, y: 1 },
    }
    : undefined;

  if (challengeEnd) {
    const challengeLabels = rootStore?.language?.translate?.MOBILE?.CHALLENGE;
    const doneCount = challengeEnd.days.filter(day => day.done).length;
    const headline = challengeEnd.completed
      ? (challengeLabels?.CONGRATULATIONS || 'Congratulations 👏')
      : doneCount <= 1
        ? (challengeLabels?.GREAT_START || 'Great start, see you tomorrow! 👋')
        : (challengeLabels?.GREAT_JOB || 'Another day done, well done! 👏');
    return (
      <ContentFrameTree
        handleExit={finishChallengeEnd}
        footer={
          <View style={{ alignSelf: 'center', width: '90%', maxWidth: 600 }}>
            <DesignSystemButton
              label={rootStore?.language?.translate?.MOBILE?.SESSION?.FINISH || 'Finish'}
              onPress={finishChallengeEnd}
            />
          </View>
        }
      >
        <View style={{ flex: 1, width: '100%', maxWidth: contentMaxWidth, alignSelf: 'center' }}>
          <ChallengeCongratsFrame
            headline={headline}
            subtitle={challengeEnd.completed ? (challengeLabels?.ONE_STEP_CLOSER || 'You completed every day of this challenge. It is up to you now to make it a habit!') : undefined}
            days={challengeEnd.days}
            designSystemColor={challengeEnd.designSystemColor}
            completed={challengeEnd.completed}
          />
        </View>
      </ContentFrameTree>
    );
  }

  const pathLabels = rootStore?.language?.translate?.MOBILE?.PATH;
  return (
    <>
      <ContentFrameTree
        handleExit={handleExit}
        linearGradient={assessmentGradient}
        // Continue/Finish stays on screen whatever the frame's height (a journal
        // field, a long memo): it lives outside the scrolling content
        footer={loading ? undefined : <SessionButtons onNext={handleOnNext} placement="footer" />}
      >
        <Show
          when={!loading}
          fallback={
            <Animated.View entering={BounceIn.duration(600)} >
              <View style={{height: '100%', width: '100%', alignItems: 'center', justifyContent: 'center'}}>
                <ActivityIndicator size={50} color={COLORS.content.accent} />
              </View>
            </Animated.View>
          }
        >
          {/* readable column on wide screens; backgrounds stay full-bleed on the frame.
            A Pressable (not a View) so a tap on empty space anywhere around a
            frame drops the keyboard — text-entry frames (journal, sleep diary)
            otherwise trap the user behind it. Taps on the frame's own
            interactive children are not intercepted. */}
          <Pressable
            onPress={isWeb ? undefined : Keyboard.dismiss}
            accessible={false}
            style={{ flex: 1, width: '100%', maxWidth: contentMaxWidth, alignSelf: 'center' }}
          >
            <Container
              onNext={handleOnNext}
              onFinish={onFinish}
            >
              {state => (
                <Presentation
                  onNext={state.onNext}
                  onFinish={state.onFinish}
                  data={state.data}
                  labels={state.labels}
                />
              )}
            </Container>
          </Pressable>
        </Show>
      </ContentFrameTree>
      <ConfirmDialog
        visible={switchPathPrompt}
        title={pathLabels?.SWITCH_PATH_TITLE || 'Make this your active path?'}
        message={pathLabels?.SWITCH_PATH_MESSAGE || 'Your Today list follows one path at a time. Switch to this one?'}
        cancelText={pathLabels?.SWITCH_PATH_CANCEL || 'Not now'}
        confirmText={pathLabels?.SWITCH_PATH_CONFIRM || 'Yes, switch'}
        onCancel={() => {
          setSwitchPathPrompt(false);
          goTo.path(pathId as string);
        }}
        onConfirm={() => {
          setSwitchPathPrompt(false);
          activatePath();
          goTo.dashboard();
        }}
      />
    </>
  );
};

export const Session = observer(_Session);
